
import React, { useState, useEffect } from 'react';
import Button from './Button';
import CookiePreferences from './CookiePreferences';
import { Cookie, Settings } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cookieManager, type ConsentData } from '../utils/cookieManager';

const CookieBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);

  useEffect(() => {
    // Show banner if no valid consent exists
    if (!cookieManager.hasValidConsent()) {
      setIsVisible(true);
    }
  }, []);

  const handleAcceptAll = () => {
    const allConsent = {
      essential: true,
      analytics: true,
      marketing: true,
      preferences: true
    };
    cookieManager.setConsent(allConsent);
    setIsVisible(false);
  };

  const handleRejectAll = () => {
    const essentialOnly = {
      essential: true,
      analytics: false,
      marketing: false,
      preferences: false
    };
    cookieManager.setConsent(essentialOnly);
    setIsVisible(false);
  };

  const handleSavePreferences = (preferences: Omit<ConsentData, 'version' | 'timestamp' | 'expiryDate'>) => {
    cookieManager.setConsent(preferences);
    setIsVisible(false);
  };

  const openPreferences = () => {
    setShowPreferences(true);
  };

  if (!isVisible && !showPreferences) return null;

  return (
    <>
      {isVisible && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-2xl">
          <div className="container-custom py-6">
            <div className="flex flex-col lg:flex-row items-start lg:items-center gap-6">
              <div className="flex items-start space-x-4 flex-1">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-primary-brand rounded-xl flex items-center justify-center shadow-md">
                    <Cookie className="w-6 h-6 text-white" />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-primary-brand text-lg mb-2">
                    Ihre Privatsphäre ist uns wichtig
                  </h3>
                  <p className="text-gray-700 text-sm leading-relaxed mb-3">
                    Wir verwenden Cookies für eine bessere Nutzererfahrung. Notwendige Cookies sind immer aktiv. 
                    Sie können Ihre Einstellungen jederzeit in unserer{' '}
                    <Link 
                      to="/datenschutz" 
                      className="text-primary-brand hover:text-secondary-brand transition-colors underline font-medium"
                    >
                      Datenschutzerklärung
                    </Link> anpassen.
                  </p>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3 lg:flex-shrink-0 min-w-fit">
                <Button
                  variant="outline"
                  onClick={openPreferences}
                  icon={<Settings className="w-4 h-4" />}
                  className="whitespace-nowrap order-1 sm:order-none"
                >
                  Einstellungen
                </Button>
                <Button
                  variant="outline"
                  onClick={handleRejectAll}
                  className="whitespace-nowrap"
                >
                  Nur notwendige
                </Button>
                <Button
                  variant="primary"
                  onClick={handleAcceptAll}
                  className="whitespace-nowrap"
                >
                  Alle akzeptieren
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <CookiePreferences
        isOpen={showPreferences}
        onClose={() => setShowPreferences(false)}
        onSave={handleSavePreferences}
      />
    </>
  );
};

export default CookieBanner;
